import {
  getSyncQueue,
  removeSyncQueueItem,
  incrementSyncRetry,
  getSnaresBySyncStatus,
  getCarcassesBySyncStatus,
  updateSnareSyncStatus,
  updateCarcassSyncStatus,
  reconcileSnare,
  reconcileCarcass,
} from './db';

const MAX_RETRY_COUNT = 3;
const API_BASE_URL = '/api';

interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

export class SyncService {
  private isSyncing = false;
  private syncInterval: number | null = null;

  /**
   * Start automatic syncing when online
   */
  start(intervalMs: number = 30000): void {
    this.stop();
    this.syncInterval = window.setInterval(() => {
      if (navigator.onLine) {
        void this.syncAll();
      }
    }, intervalMs);

    // Also sync immediately if online
    if (navigator.onLine) {
      void this.syncAll();
    }
  }

  /**
   * Stop automatic syncing
   */
  stop(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Manually trigger a full sync
   */
  async syncAll(): Promise<SyncResult> {
    if (this.isSyncing) {
      return {
        success: false,
        synced: 0,
        failed: 0,
        errors: ['Sync already in progress'],
      };
    }

    if (!navigator.onLine) {
      return {
        success: false,
        synced: 0,
        failed: 0,
        errors: ['Device is offline'],
      };
    }

    this.isSyncing = true;
    const errors: string[] = [];
    let syncedCount = 0;
    let failedCount = 0;

    try {
      // Process sync queue
      const queueResult = await this.processSyncQueue();
      syncedCount += queueResult.synced;
      failedCount += queueResult.failed;
      errors.push(...queueResult.errors);

      // Sync pending snares
      const snaresResult = await this.syncPendingSnares();
      syncedCount += snaresResult.synced;
      failedCount += snaresResult.failed;
      errors.push(...snaresResult.errors);

      // Sync pending carcasses
      const carcassesResult = await this.syncPendingCarcasses();
      syncedCount += carcassesResult.synced;
      failedCount += carcassesResult.failed;
      errors.push(...carcassesResult.errors);

      return {
        success: failedCount === 0,
        synced: syncedCount,
        failed: failedCount,
        errors,
      };
    } finally {
      this.isSyncing = false;
    }
  }

  private async processSyncQueue(): Promise<SyncResult> {
    const queue = await getSyncQueue();
    let synced = 0;
    let failed = 0;
    const errors: string[] = [];

    // Get auth token
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    // Only add auth header if token exists and is not the string "null"
    if (token && token !== 'null') {
      headers['Authorization'] = `Bearer ${token}`;
    }

    for (const item of queue) {
      try {
        if (item.retryCount >= MAX_RETRY_COUNT) {
          errors.push(`Max retries exceeded for ${item.resource} ${item.resourceId}`);
          failed++;
          continue;
        }

        const endpoint = `/api/${item.resource}s`; // snares or carcasses
        let response: Response;

        switch (item.operation) {
          case 'create':
            response = await fetch(endpoint, {
              method: 'POST',
              headers,
              body: JSON.stringify(item.data),
              credentials: 'include',
            });
            break;

          case 'update':
            response = await fetch(`${endpoint}/${item.resourceId}`, {
              method: 'PUT',
              headers,
              body: JSON.stringify(item.data),
              credentials: 'include',
            });
            break;

          case 'delete':
            response = await fetch(`${endpoint}/${item.resourceId}`, {
              method: 'DELETE',
              headers,
              credentials: 'include',
            });
            break;
        }

        if (response!.ok) {
          await removeSyncQueueItem(item.id);
          synced++;
        } else {
          const error = `HTTP ${response!.status}: ${await response!.text()}`;
          await incrementSyncRetry(item.id, error);
          errors.push(error);
          failed++;
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        await incrementSyncRetry(item.id, errorMsg);
        errors.push(`Failed to sync ${item.resource} ${item.resourceId}: ${errorMsg}`);
        failed++;
      }
    }

    return { success: failed === 0, synced, failed, errors };
  }

  private async syncPendingSnares(): Promise<SyncResult> {
    const pendingSnares = await getSnaresBySyncStatus('pending');
    let synced = 0;
    let failed = 0;
    const errors: string[] = [];

    // Get auth token
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    // Only add auth header if token exists and is not the string "null"
    if (token && token !== 'null') {
      headers['Authorization'] = `Bearer ${token}`;
    }

    for (const snare of pendingSnares) {
      try {
        const response = await fetch(`${API_BASE_URL}/snares`, {
          method: 'POST',
          headers,
          body: JSON.stringify(snare),
          credentials: 'include',
        });

        if (response.ok) {
          // Reconcile with the server's canonical record (id, snareNumber, photo URLs)
          try {
            const body = await response.json();
            if (body && body.snare) {
              await reconcileSnare(snare.id, body.snare);
            } else {
              await updateSnareSyncStatus(snare.id, 'synced');
            }
          } catch {
            await updateSnareSyncStatus(snare.id, 'synced');
          }
          synced++;
        } else {
          const error = `HTTP ${response.status}: ${await response.text()}`;
          await updateSnareSyncStatus(snare.id, 'failed');
          errors.push(`Snare ${snare.id}: ${error}`);
          failed++;
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        await updateSnareSyncStatus(snare.id, 'failed');
        errors.push(`Snare ${snare.id}: ${errorMsg}`);
        failed++;
      }
    }

    return { success: failed === 0, synced, failed, errors };
  }

  private async syncPendingCarcasses(): Promise<SyncResult> {
    const pendingCarcasses = await getCarcassesBySyncStatus('pending');
    let synced = 0;
    let failed = 0;
    const errors: string[] = [];

    // Get auth token
    const token = localStorage.getItem('auth_token');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    // Only add auth header if token exists and is not the string "null"
    if (token && token !== 'null') {
      headers['Authorization'] = `Bearer ${token}`;
    }

    for (const carcass of pendingCarcasses) {
      try {
        const response = await fetch(`${API_BASE_URL}/carcasses`, {
          method: 'POST',
          headers,
          body: JSON.stringify(carcass),
          credentials: 'include',
        });

        if (response.ok) {
          // Reconcile with the server's canonical record (id, carcassNumber, photo URLs)
          try {
            const body = await response.json();
            if (body && body.carcass) {
              await reconcileCarcass(carcass.id, body.carcass);
            } else {
              await updateCarcassSyncStatus(carcass.id, 'synced');
            }
          } catch {
            await updateCarcassSyncStatus(carcass.id, 'synced');
          }
          synced++;
        } else {
          const error = `HTTP ${response.status}: ${await response.text()}`;
          await updateCarcassSyncStatus(carcass.id, 'failed');
          errors.push(`Carcass ${carcass.id}: ${error}`);
          failed++;
        }
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        await updateCarcassSyncStatus(carcass.id, 'failed');
        errors.push(`Carcass ${carcass.id}: ${errorMsg}`);
        failed++;
      }
    }

    return { success: failed === 0, synced, failed, errors };
  }

  /**
   * Get sync status summary
   */
  async getStatus(): Promise<{
    pending: { snares: number; carcasses: number; queue: number };
    synced: { snares: number; carcasses: number };
    failed: { snares: number; carcasses: number };
  }> {
    const [pendingSnares, syncedSnares, failedSnares] = await Promise.all([
      getSnaresBySyncStatus('pending'),
      getSnaresBySyncStatus('synced'),
      getSnaresBySyncStatus('failed'),
    ]);

    const [pendingCarcasses, syncedCarcasses, failedCarcasses] = await Promise.all([
      getCarcassesBySyncStatus('pending'),
      getCarcassesBySyncStatus('synced'),
      getCarcassesBySyncStatus('failed'),
    ]);

    const queue = await getSyncQueue();

    return {
      pending: {
        snares: pendingSnares.length,
        carcasses: pendingCarcasses.length,
        queue: queue.length,
      },
      synced: {
        snares: syncedSnares.length,
        carcasses: syncedCarcasses.length,
      },
      failed: {
        snares: failedSnares.length,
        carcasses: failedCarcasses.length,
      },
    };
  }
}

// Export singleton instance
export const syncService = new SyncService();
